import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type TimelineSource = "smartfile" | "trainingplan" | "gallery" | "pilates" | "heartrisk";

export interface TimelineDeleteItem {
  id: string;
  source: TimelineSource;
  contentTypeLabel: string;
  sessionId?: string;
  physioGlobalGroup?: unknown;
  galleryImageId?: string;
  galleryFilePath?: string;
  trainingPlanId?: string;
  heartRiskCalculationId?: string;
  pilatesRecordId?: string;
}

interface SmartfileSessionRef {
  id: string;
  content_type?: string | null;
  structured_records?: unknown[] | null;
}

function isTrainingPlanStructuredRecord(record: unknown): boolean {
  if (!record || typeof record !== "object") return false;
  return (record as Record<string, unknown>).type === "training_plan";
}

function sessionHasTrainingPlanContent(session: SmartfileSessionRef): boolean {
  if (session.content_type === "training_plan") return true;
  if (!Array.isArray(session.structured_records)) return false;
  return session.structured_records.some(isTrainingPlanStructuredRecord);
}

export function getTrainingPlanSessionIds(sessions: SmartfileSessionRef[]): string[] {
  const ids = new Set<string>();
  for (const session of sessions) {
    if (sessionHasTrainingPlanContent(session)) {
      ids.add(session.id);
    }
  }
  return Array.from(ids);
}

function getTrainingPlanIdFromStructuredRecord(record: unknown): string | null {
  if (!record || typeof record !== "object") return null;
  const r = record as Record<string, unknown>;
  const candidates = [r.training_plan_id, r.plan_id, r.planId, r.id];
  const valid = candidates.find((value) => typeof value === "string" && value.trim().length > 0);
  return typeof valid === "string" ? valid.trim() : null;
}

async function deleteTrainingPlanSmartfileSessions(
  client: SupabaseClient<Database>,
  sessions: SmartfileSessionRef[],
  trainingPlanId?: string,
): Promise<void> {
  const sessionIds = new Set(getTrainingPlanSessionIds(sessions));

  if (trainingPlanId) {
    for (const session of sessions) {
      if (!Array.isArray(session.structured_records)) continue;
      const isLinked = session.structured_records.some((record) => {
        return getTrainingPlanIdFromStructuredRecord(record) === trainingPlanId;
      });
      if (isLinked) {
        sessionIds.add(session.id);
      }
    }
  }

  for (const sessionId of sessionIds) {
    await deleteSmartfileSession(client, sessionId);
  }
}

function isPhysioSubRecord(record: unknown): boolean {
  if (!record || typeof record !== "object") return false;
  const r = record as Record<string, unknown>;
  return (
    r.type === "physiotherapy_evolution" &&
    r.is_main_record !== true &&
    r.is_main_record !== "true"
  );
}

export function getPhysioSessionIds(sessions: SmartfileSessionRef[]): string[] {
  const ids = new Set<string>();
  for (const session of sessions) {
    if (!Array.isArray(session.structured_records)) continue;
    if (session.structured_records.some(isPhysioSubRecord)) {
      ids.add(session.id);
    }
  }
  return Array.from(ids);
}

export function getTimelineDeleteDescription(
  item: TimelineDeleteItem,
  physioSessionCount?: number,
): string {
  if (item.source === "gallery") {
    return "A imagem e o arquivo original serão excluídos permanentemente. Esta ação não pode ser desfeita.";
  }
  if (item.source === "trainingplan" || item.contentTypeLabel === "Plano de Treino") {
    return "O plano de treino, documentos espelhados e todos os exercícios associados serão removidos permanentemente. Esta ação não pode ser desfeita.";
  }
  if (item.source === "heartrisk") {
    return "Este cálculo de risco cardiovascular será removido permanentemente. Esta ação não pode ser desfeita.";
  }
  if (item.source === "pilates") {
    return "Esta evolução de pilates será removida permanentemente. Esta ação não pode ser desfeita.";
  }
  if (item.physioGlobalGroup) {
    const count = physioSessionCount ?? 0;
    const sessionLabel = count === 1 ? "1 documento de origem" : `${count} documentos de origem`;
    return `Todos os registros de evolução fisioterápica e ${sessionLabel} serão removidos permanentemente, incluindo arquivos originais. Esta ação não pode ser desfeita.`;
  }
  return "O documento original e todos os registros extraídos serão removidos permanentemente. Esta ação não pode ser desfeita.";
}

async function deleteSmartfileSession(
  client: SupabaseClient<Database>,
  sessionId: string,
): Promise<void> {
  const { data, error } = await client.functions.invoke("delete-smartfile-session", {
    body: { sessionId },
  });

  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
}

export async function deleteTimelineItem(
  item: TimelineDeleteItem,
  context: {
    patientId: string;
    sessions: SmartfileSessionRef[];
    supabaseClient?: SupabaseClient<Database>;
  },
): Promise<void> {
  const client = context.supabaseClient ?? supabase;

  switch (item.source) {
    case "smartfile": {
      if (item.physioGlobalGroup) {
        const sessionIds = getPhysioSessionIds(context.sessions);
        for (const sessionId of sessionIds) {
          await deleteSmartfileSession(client, sessionId);
        }
        return;
      }
      if (!item.sessionId) {
        throw new Error("Sessão não encontrada para exclusão");
      }
      if (item.contentTypeLabel === "Plano de Treino") {
        await deleteTrainingPlanSmartfileSessions(client, context.sessions, item.trainingPlanId);

        const { error: planError } = await client
          .from("training_plans")
          .delete()
          .eq("patient_id", context.patientId)
          .neq("status", "archived");

        if (planError) throw planError;
        return;
      }
      await deleteSmartfileSession(client, item.sessionId);
      return;
    }
    case "gallery": {
      if (!item.galleryImageId || !item.galleryFilePath) {
        throw new Error("Dados da imagem incompletos");
      }
      await client.storage.from("patient-docs").remove([item.galleryFilePath]);
      const { error } = await client
        .from("patient_gallery")
        .delete()
        .eq("id", item.galleryImageId);
      if (error) throw error;
      return;
    }
    case "trainingplan": {
      await deleteTrainingPlanSmartfileSessions(client, context.sessions, item.trainingPlanId);

      const { data: deletedPlans, error } = await client
        .from("training_plans")
        .delete()
        .eq("patient_id", context.patientId)
        .neq("status", "archived")
        .select("id");

      if (error) throw error;

      if (!deletedPlans?.length && item.trainingPlanId) {
        const { data: deletedSinglePlan, error: singlePlanError } = await client
          .from("training_plans")
          .delete()
          .eq("id", item.trainingPlanId)
          .eq("patient_id", context.patientId)
          .select("id");

        if (singlePlanError) throw singlePlanError;
        if (!deletedSinglePlan?.length) {
          throw new Error("Plano de treino não encontrado ou sem permissão para excluir");
        }
      }

      return;
    }
    case "heartrisk": {
      if (!item.heartRiskCalculationId) {
        throw new Error("Cálculo de risco não encontrado");
      }
      const { error } = await client
        .from("patient_heart_risk_calculations")
        .delete()
        .eq("id", item.heartRiskCalculationId)
        .eq("patient_id", context.patientId);
      if (error) throw error;
      return;
    }
    case "pilates": {
      if (!item.pilatesRecordId) {
        throw new Error("Evolução de pilates não encontrada");
      }
      const { error } = await client
        .from("pilates_session_evolutions")
        .delete()
        .eq("id", item.pilatesRecordId)
        .eq("patient_id", context.patientId);
      if (error) throw error;
      return;
    }
    default:
      throw new Error("Tipo de registro não suportado para exclusão");
  }
}
